import type { ButtonProps } from '@mui/material/Button';
import type { Theme, CSSObject, Components, ComponentsVariants } from '@mui/material/styles';

import { buttonClasses } from '@mui/material/Button';
import { loadingButtonClasses } from '@mui/lab/LoadingButton';

import { varAlpha, stylesMode } from '../../styles';

// ----------------------------------------------------------------------

// NEW VARIANT
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    soft: true;
  }
}

const COLORS = ['primary', 'secondary', 'info', 'success', 'warning', 'error'] as const;

type ColorType = (typeof COLORS)[number];

function styleColors(ownerState: ButtonProps, styles: (val: ColorType) => CSSObject) {
  const outputStyle = COLORS.reduce((acc, color) => {
    if (!ownerState.disabled && ownerState.color === color) {
      acc = styles(color);
    }
    return acc;
  }, {});

  return outputStyle;
}

function getButtonColor(ownerState: ButtonProps): ColorType {
  return COLORS.find((color) => ownerState.color === color) ?? 'primary';
}

function containedColorStyles(theme: Theme, color: ColorType): CSSObject {
  return {
    '--variant-containedColor': theme.vars.palette[color].contrastText,
    '--variant-containedBg': theme.vars.palette[color].main,
    '--variant-containedHoverBg': theme.vars.palette[color].dark,
    color: theme.vars.palette[color].contrastText,
    backgroundColor: theme.vars.palette[color].main,
    '&:hover': {
      backgroundColor: theme.vars.palette[color].dark,
      boxShadow: theme.customShadows[color],
    },
  };
}

function containedRootStyles(theme: Theme): CSSObject {
  return {
    [`&.${buttonClasses.contained}:not(.${buttonClasses.disabled})`]: containedColorStyles(
      theme,
      'primary',
    ),
    [`&.${buttonClasses.containedPrimary}:not(.${buttonClasses.disabled})`]: containedColorStyles(
      theme,
      'primary',
    ),
    [`&.${buttonClasses.containedSecondary}:not(.${buttonClasses.disabled})`]: containedColorStyles(
      theme,
      'secondary',
    ),
    [`&.${buttonClasses.containedInfo}:not(.${buttonClasses.disabled})`]: containedColorStyles(
      theme,
      'info',
    ),
    [`&.${buttonClasses.containedSuccess}:not(.${buttonClasses.disabled})`]: containedColorStyles(
      theme,
      'success',
    ),
    [`&.${buttonClasses.containedWarning}:not(.${buttonClasses.disabled})`]: containedColorStyles(
      theme,
      'warning',
    ),
    [`&.${buttonClasses.containedError}:not(.${buttonClasses.disabled})`]: containedColorStyles(
      theme,
      'error',
    ),
    [`&.${buttonClasses.contained}.${buttonClasses.disabled}`]: {
      '--variant-containedColor': theme.vars.palette.text.disabled,
      '--variant-containedBg': theme.vars.palette.grey[300],
      color: theme.vars.palette.text.disabled,
      backgroundColor: theme.vars.palette.grey[300],
      boxShadow: 'none',
      [stylesMode.dark]: {
        '--variant-containedBg': theme.vars.palette.grey[700],
        backgroundColor: theme.vars.palette.grey[700],
      },
    },
  };
}

// ----------------------------------------------------------------------

const MuiButtonBase: Components<Theme>['MuiButtonBase'] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: { root: ({ theme }) => ({ fontFamily: theme.typography.fontFamily }) },
};

// ----------------------------------------------------------------------

const softVariant: Record<string, ComponentsVariants<Theme>['MuiButton']> = {
  colors: COLORS.map((color) => ({
    props: ({ ownerState }) =>
      !ownerState.disabled && ownerState.variant === 'soft' && ownerState.color === color,
    style: ({ theme }) => ({
      color: theme.vars.palette[color].dark,
      backgroundColor: varAlpha(theme.vars.palette[color].mainChannel, 0.16),
      '&:hover': { backgroundColor: varAlpha(theme.vars.palette[color].mainChannel, 0.32) },
      [stylesMode.dark]: { color: theme.vars.palette[color].light },
    }),
  })),
  base: [
    {
      props: ({ ownerState }) => ownerState.variant === 'soft',
      style: ({ theme }) => ({
        backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
        '&:hover': { backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.24) },
        [`&.${buttonClasses.disabled}`]: {
          backgroundColor: theme.vars.palette.grey[300],
          [stylesMode.dark]: {
            backgroundColor: theme.vars.palette.grey[700],
          },
        },
        [`& .${loadingButtonClasses.loadingIndicatorStart}`]: { left: 14 },
        [`& .${loadingButtonClasses.loadingIndicatorEnd}`]: { right: 14 },
        [`&.${buttonClasses.sizeSmall}`]: {
          [`& .${loadingButtonClasses.loadingIndicatorStart}`]: { left: 10 },
          [`& .${loadingButtonClasses.loadingIndicatorEnd}`]: { right: 10 },
        },
      }),
    },
  ],
};

const MuiButton: Components<Theme>['MuiButton'] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  defaultProps: { color: 'primary', disableElevation: true },

  /** **************************************
   * VARIANTS
   *************************************** */
  variants: [
    /**
     * @variant soft
     */
    ...[...softVariant.base!, ...softVariant.colors!],
  ],

  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => containedRootStyles(theme),
    /**
     * @variant contained
     */
    contained: ({ theme, ownerState }) => {
      const color = getButtonColor(ownerState);
      const styled = {
        disabled: {
          [`&.${buttonClasses.disabled}`]: {
            color: theme.vars.palette.text.disabled,
            backgroundColor: theme.vars.palette.grey[300],
            [stylesMode.dark]: {
              backgroundColor: theme.vars.palette.grey[700],
            },
          },
        },
        colors: !ownerState.disabled && ownerState.color !== 'inherit' ? {
          ...containedColorStyles(theme, color),
        } : {},
        inheritColor: {
          ...(ownerState.color === 'inherit' &&
            !ownerState.disabled && {
              color: theme.vars.palette.common.white,
              backgroundColor: theme.vars.palette.grey[800],
              '&:hover': {
                boxShadow: theme.customShadows.z8,
                backgroundColor: theme.vars.palette.grey[700],
              },
              [stylesMode.dark]: {
                color: theme.vars.palette.grey[800],
                backgroundColor: theme.vars.palette.common.white,
                '&:hover': { backgroundColor: theme.vars.palette.grey[400] },
              },
            }),
        },
      };
      return { ...styled.disabled, ...styled.inheritColor, ...styled.colors };
    },
    /**
     * @variant outlined
     */
    outlined: ({ theme, ownerState }) => {
      const styled = {
        colors: styleColors(ownerState, (color) => ({
          borderColor: varAlpha(theme.vars.palette[color].mainChannel, 0.48),
        })),
        inheritColor: {
          ...(ownerState.color === 'inherit' &&
            !ownerState.disabled && {
              borderColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.32),
              '&:hover': { backgroundColor: theme.vars.palette.action.hover },
            }),
        },
        base: {
          '&:hover': { borderColor: 'currentColor', boxShadow: '0 0 0 0.75px currentColor' },
        },
      };
      return { ...styled.base, ...styled.inheritColor, ...styled.colors };
    },
    /**
     * @variant text
     */
    text: ({ ownerState, theme }) => {
      const styled = {
        inheritColor: {
          ...(ownerState.color === 'inherit' &&
            !ownerState.disabled && {
              '&:hover': { backgroundColor: theme.vars.palette.action.hover },
            }),
        },
      };
      return { ...styled.inheritColor };
    },
    /**
     * @size
     */
    sizeSmall: ({ ownerState }) => ({
      height: 30,
      ...(ownerState.variant === 'text'
        ? { paddingLeft: '4px', paddingRight: '4px' }
        : { paddingLeft: '8px', paddingRight: '8px' }),
    }),
    sizeMedium: ({ ownerState }) => ({
      ...(ownerState.variant === 'text'
        ? { paddingLeft: '8px', paddingRight: '8px' }
        : { paddingLeft: '12px', paddingRight: '12px' }),
    }),
    sizeLarge: ({ ownerState }) => ({
      height: 48,
      ...(ownerState.variant === 'text'
        ? { paddingLeft: '10px', paddingRight: '10px' }
        : { paddingLeft: '16px', paddingRight: '16px' }),
    }),
  },
};

// ----------------------------------------------------------------------

export const button = { MuiButtonBase, MuiButton };
